import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {biobookApi} from '../../app/services/biobookApi';
import {identitiesApi} from '../../app/services/identitiesApi';
import {logout} from '../auth/authSlice';
import {BiobookFilter} from '../biobook/types';

export interface PersonState {
  allPerioperativeVisibleConditionIds: number[];
  selectedBiobookPageInfo: {id: number; timestamp: number} | null;
  selectedConditionIds: number[];
  selectedFilter: BiobookFilter | null;
}

const initialState: PersonState = {
  allPerioperativeVisibleConditionIds: [],
  selectedBiobookPageInfo: null,
  selectedConditionIds: [],
  selectedFilter: null,
};

export const personSlice = createSlice({
  name: 'person',
  initialState,
  reducers: {
    deselectBiobookPage: (state) => {
      state.selectedBiobookPageInfo = null;
    },
    deselectCondition: (state, action: PayloadAction<number>) => {
      const index = state.selectedConditionIds.indexOf(action.payload);
      if (index !== -1) {
        state.selectedConditionIds.splice(index, 1);
      }
    },
    selectBiobookPage: (state, action: PayloadAction<number>) => {
      state.selectedBiobookPageInfo = {id: action.payload, timestamp: Date.now()};
      state.selectedConditionIds = [];
      state.selectedFilter = null;
    },
    selectCondition: (state, action: PayloadAction<number>) => {
      state.selectedConditionIds.push(action.payload);
    },
    setAllPerioperativeVisibleConditionIds: (state, action: PayloadAction<number[]>) => {
      state.allPerioperativeVisibleConditionIds = action.payload;
    },
    setSelectedConditionIds: (state, action: PayloadAction<number[]>) => {
      state.selectedConditionIds = action.payload;
    },
    setSelectedFilter: (state, action: PayloadAction<BiobookFilter | null>) => {
      state.selectedFilter = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => {
      return initialState;
    });
    builder.addMatcher(biobookApi.endpoints.deleteBiobookUpdates.matchFulfilled, (state) => {
      if (state.selectedFilter === 'updates') {
        state.selectedFilter = null;
      }
    });
    builder.addMatcher(biobookApi.endpoints.setBiobookPreferences.matchPending, (state, action) => {
      if (!action.meta.arg.originalArgs.biobookPreferences.favoritePhraseIds.length) {
        if (state.selectedFilter === 'favorites') {
          state.selectedFilter = null;
        }
      }
    });
    builder.addMatcher(identitiesApi.endpoints.registerVerifyOTP.matchFulfilled, () => {
      return initialState;
    });
  },
});

export const {
  deselectBiobookPage,
  deselectCondition,
  selectBiobookPage,
  selectCondition,
  setAllPerioperativeVisibleConditionIds,
  setSelectedConditionIds,
  setSelectedFilter,
} = personSlice.actions;

export const selectAllPerioperativeVisibleConditionIds = (state: {person: PersonState}) =>
  state.person.allPerioperativeVisibleConditionIds;

export const selectSelectedBiobookPageInfo = (state: {person: PersonState}) => state.person.selectedBiobookPageInfo;

export const selectSelectedConditionIds = (state: {person: PersonState}) => state.person.selectedConditionIds;

export const selectSelectedFilter = (state: {person: PersonState}) => state.person.selectedFilter;

export default personSlice.reducer;
